import { useApi } from '@omnipresentgroup/api';
import {
  type CombinedEmployee,
  CombinedEmployeeType,
  type GetEmployeesSimplifiedAPIResponse,
  type GetSeatsAPIResponse,
} from '@omnipresentgroup/types';
import { useQueries } from '@tanstack/react-query';

import { API_BASE, API_PATH, SEATS_API_PATH } from '../constants';

export const useEmployees = ({ companyId }: { companyId: string }) => {
  const { GET } = useApi();

  const query = useQueries({
    queries: [
      {
        queryKey: [API_PATH, companyId],
        queryFn: () =>
          GET<GetEmployeesSimplifiedAPIResponse>(
            `${API_BASE}/${API_PATH}/company/${companyId}`,
            {},
            {
              simplified: 'true',
            },
          ),
        enabled: !!companyId,
        select: (data: GetEmployeesSimplifiedAPIResponse) => data.employees,
      },
      {
        queryKey: [SEATS_API_PATH, companyId],
        queryFn: () =>
          GET<GetSeatsAPIResponse>(
            `${API_BASE}/${SEATS_API_PATH}/company/${companyId}`,
          ),
        enabled: !!companyId,
      },
    ],
    combine: (results) => {
      const [employeesResult, seatsResult] = results;

      const employees = employeesResult.data || [];
      const seats = seatsResult.data || [];

      const combinedData: CombinedEmployee[] = [
        ...employees.map((employee) => ({
          id: employee.id,
          name: employee.name,
          jobTitle: employee.jobTitle,
          country: employee.country,
          startDate: employee.startDate,
          nextCutoffDate: employee.nextCutoffDate,
          annualBaseSalary: {
            amount: employee.annualBaseSalary,
            currency: employee.currency,
          },
          onboardingStatus: employee.onboardingStatus,
          type: CombinedEmployeeType.EMPLOYEE as const,
        })),
        ...seats.map((seat) => ({
          id: seat.id,
          name: `${seat.firstName} ${seat.lastName}`.trim() || '',
          jobTitle: '',
          country: seat.countryName,
          startDate: seat.desiredStartDate || '',
          annualBaseSalary: seat.annualBaseSalary || {
            amount: seat.employeeProfile?.annualBaseSalaryAmount || '',
            currency: seat.employeeProfile?.annualBaseSalaryCurrency || '',
          },
          onboardingStatus: seat.sowStatus,
          type: CombinedEmployeeType.PLANNED_HIRE as const,
        })),
      ];

      return {
        data: combinedData,
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });

  return query;
};
